"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import type { SiteContentByLocale } from "@/lib/content/pages/site";

type NavItem = {
  key: string;
  href: string;
  dropdown?: { key: string; href: string }[];
};

const NAV_ITEMS: NavItem[] = [
  {
    key: "membership",
    href: "/membership",
    dropdown: [
      { key: "membership.private", href: "/membership/private" },
      { key: "membership.corporate", href: "/membership/corporate" },
    ],
  },
  {
    key: "services",
    href: "/services",
    dropdown: [
      { key: "services.lifestyle", href: "/services/lifestyle" },
      { key: "services.corporate", href: "/services/corporate" },
    ],
  },
  { key: "events", href: "/events" },
  { key: "destinations", href: "/destinations" },
  { key: "about", href: "/about" },
  { key: "contact", href: "/contact" },
];

const LABELS: Record<"fr" | "en", Record<string, string>> = {
  fr: {
    membership: "Adhésion",
    "membership.private": "Adhésion Privée",
    "membership.corporate": "Adhésion Corporate",
    services: "Services",
    "services.lifestyle": "Lifestyle",
    "services.corporate": "Corporate",
    events: "Événements",
    destinations: "Destinations",
    about: "À propos",
    contact: "Contact",
    becomeMember: "Devenir membre",
  },
  en: {
    membership: "Membership",
    "membership.private": "Private Membership",
    "membership.corporate": "Corporate Membership",
    services: "Services",
    "services.lifestyle": "Lifestyle",
    "services.corporate": "Corporate",
    events: "Events",
    destinations: "Destinations",
    about: "About",
    contact: "Contact",
    becomeMember: "Become a member",
  },
};

/** Libellés issus du contenu éditable (clés identiques à LABELS). */
function labelsFromContent(n: SiteContentByLocale["fr"]["nav"]): Record<string, string> {
  return {
    membership: n.membership,
    "membership.private": n.membershipPrivate,
    "membership.corporate": n.membershipCorporate,
    services: n.services,
    "services.lifestyle": n.servicesLifestyle,
    "services.corporate": n.servicesCorporate,
    events: n.events,
    destinations: n.destinations,
    about: n.about,
    contact: n.contact,
    becomeMember: n.becomeMember,
  };
}

function ChevronDown({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowUpRight({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <path d="M7 17L17 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M10 7h7v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function normalizeLocale(pathname: string | null): "fr" | "en" {
  if (!pathname) return "fr";
  const seg = pathname.split("/").filter(Boolean)[0];
  return seg === "en" ? "en" : "fr";
}

function stripLocale(pathname: string | null) {
  if (!pathname) return "/";
  const parts = pathname.split("/").filter(Boolean);
  const first = parts[0];
  if (first === "fr" || first === "en") {
    const rest = parts.slice(1);
    return "/" + rest.join("/");
  }
  return pathname;
}

export default function Header({ content }: { content?: SiteContentByLocale }) {
  const pathname = usePathname();
  const locale = useMemo(() => normalizeLocale(pathname), [pathname]);
  const basePath = useMemo(() => stripLocale(pathname), [pathname]);
  const navContent = content?.[locale]?.nav;
  const labels = useMemo(
    () => (navContent ? labelsFromContent(navContent) : LABELS[locale]),
    [navContent, locale]
  );
  const logoSrc = navContent?.logo || "/WC-Menu-logo.png";

  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [pathname]);

  // Verrouille le scroll de la page quand le drawer mobile est ouvert
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const linkForLocale = (targetLocale: "fr" | "en") => {
    const suffix = basePath.startsWith("/") ? basePath : `/${basePath}`;
    return `/${targetLocale}${suffix === "/" ? "" : suffix}`;
  };

  const navLinkBase =
    "text-[12px] font-light uppercase tracking-[0.08em] transition";

  // Item de menu actif = page courante (gère les sous-pages, ex: /membership/private)
  const isActiveItem = (itemHref: string) =>
    itemHref === "/"
      ? basePath === "/"
      : basePath === itemHref || basePath.startsWith(`${itemHref}/`);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-[1400px] px-6 pt-4 transition-all duration-300">
        <div
          className={[
            "relative flex h-14 items-center rounded-full px-5",
            "bg-black/45 backdrop-blur-md border border-white/10",
            scrolled
              ? "shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
              : "shadow-[0_10px_30px_rgba(0,0,0,0.28)]",
          ].join(" ")}
        >
          {/* Left - Logo */}
          <div className="flex min-w-0 items-center gap-3 md:min-w-[180px]">
            <Link href={`/${locale}`} className="inline-flex items-center" aria-label="Welconcept">
              <Image src={logoSrc} alt="Welconcept" width={180} height={40} className="h-8 w-auto" priority />
            </Link>
          </div>

          {/* Center - Navigation */}
          <nav className="hidden flex-1 items-center justify-center md:flex">
            <ul className="flex items-center gap-10">
              {NAV_ITEMS.map((item) => {
                const hasDropdown = !!item.dropdown?.length;
                const isOpen = openMenu === item.key;
                const label = labels[item.key] ?? item.key;
                const active = isActiveItem(item.href);
                const colorCls = active
                  ? "text-white"
                  : "text-white/80 hover:text-white";

                // underline + arrow flip ONLY for Membership & Services
                const fancyHover = item.key === "membership" || item.key === "services";

                return (
                  <li
                    key={item.key}
                    className="relative"
                    onMouseEnter={() => hasDropdown && setOpenMenu(item.key)}
                    onMouseLeave={() => hasDropdown && setOpenMenu(null)}
                  >
                    {hasDropdown ? (
                      <button
                        type="button"
                        className={[
                          fancyHover ? "group inline-flex items-center gap-2" : "inline-flex items-center gap-2",
                          navLinkBase,
                          colorCls,
                          "relative",
                        ].join(" ")}
                        onClick={() => setOpenMenu(isOpen ? null : item.key)}
                        aria-expanded={isOpen}
                      >
                        <span>{label}</span>

                        {/* Arrow flip */}
                        <ChevronDown
                          className={[
                            "h-4 w-4 text-white/70 transition-transform duration-200",
                            fancyHover ? "group-hover:rotate-180" : "",
                            isOpen ? "rotate-180" : "",
                          ].join(" ")}
                        />

                        {/* Underline (only fancy items) */}
                        {fancyHover && (
                          <span
                            className={[
                              "pointer-events-none absolute -bottom-[10px] left-0 h-[1px] w-full",
                              "origin-left scale-x-0 bg-white/55 transition-transform duration-200",
                              "group-hover:scale-x-100",
                              isOpen || active ? "scale-x-100" : "",
                            ].join(" ")}
                          />
                        )}
                      </button>
                    ) : (
                      <>
                        <Link
                          href={`/${locale}${item.href}`}
                          className={[navLinkBase, colorCls].join(" ")}
                        >
                          {label}
                        </Link>
                        {active && (
                          <span className="pointer-events-none absolute -bottom-[10px] left-0 h-px w-full bg-white/55" />
                        )}
                      </>
                    )}

                    {hasDropdown && isOpen && (
                      // Wrapper anchored flush to the trigger (top-full) with a
                      // padding-top "bridge" so the hover area stays continuous —
                      // without this gap the pointer leaves the <li> and the menu
                      // closes before reaching the links.
                      <div className="absolute left-1/2 top-full -translate-x-1/2 pt-[10px]">
                        <div
                          className={[
                            "min-w-[240px] rounded-2xl border border-white/10 bg-black/70 backdrop-blur-md",
                            "p-2 shadow-[0_20px_60px_rgba(0,0,0,0.55)]",
                          ].join(" ")}
                        >
                          <div className="grid gap-1">
                            {item.dropdown!.map((d) => (
                              <Link
                                key={d.href}
                                href={`/${locale}${d.href}`}
                                className={[
                                  "group flex items-center justify-between rounded-xl px-3 py-2.5",
                                  "text-[12px] font-light uppercase tracking-[0.06em]",
                                  "text-white/80 hover:bg-white/10 hover:text-white",
                                ].join(" ")}
                              >
                                <span>{labels[d.key] ?? d.key}</span>
                                <ArrowUpRight className="h-4 w-4 text-white/60 opacity-0 transition group-hover:opacity-100" />
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Right - CTA + Locale (desktop) / Hamburger (mobile) */}
          <div className="ml-auto flex min-w-0 items-center justify-end gap-4 md:min-w-[180px]">
            <Link
              href={`/${locale}/membership/join`}
              className={[
                "hidden whitespace-nowrap rounded-full bg-white px-6 py-2",
                "text-[11px] font-light uppercase text-black",
                "transition hover:bg-white/90 md:inline-flex",
              ].join(" ")}
            >
              {labels.becomeMember}
            </Link>

            <div className="hidden items-center gap-2 text-[12px] font-light uppercase tracking-[0.08em] text-white/70 md:flex">
              <Link
                href={linkForLocale("fr")}
                className={locale === "fr" ? "text-white" : "hover:text-white"}
              >
                FR
              </Link>
              <span className="text-white/35">/</span>
              <Link
                href={linkForLocale("en")}
                className={locale === "en" ? "text-white" : "hover:text-white"}
              >
                EN
              </Link>
            </div>

            {/* Hamburger — mobile uniquement */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={locale === "fr" ? "Ouvrir le menu" : "Open menu"}
              aria-expanded={mobileOpen}
              className="inline-flex h-9 w-9 flex-col items-center justify-center gap-[5px] md:hidden"
            >
              <span className="h-px w-5 bg-white/90" />
              <span className="h-px w-5 bg-white/90" />
              <span className="h-px w-3.5 self-end mr-2 bg-white/60" />
            </button>
          </div>
        </div>
      </div>

      {/* ── MENU MOBILE — drawer depuis la droite ── */}
      {/* Voile */}
      <div
        onClick={() => setMobileOpen(false)}
        className={[
          "fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm transition-opacity duration-500 md:hidden",
          mobileOpen ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
        aria-hidden
      />

      {/* Panneau */}
      <aside
        className={[
          "fixed right-0 top-0 z-[70] flex h-dvh w-[86%] max-w-[360px] flex-col md:hidden",
          "border-l border-white/10 bg-[#0b0b0d]/95 backdrop-blur-xl",
          "transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
          mobileOpen ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
        aria-hidden={!mobileOpen}
      >
        {/* En-tête du panneau */}
        <div className="flex items-center justify-between border-b border-white/10 px-7 py-5">
          <span className="text-[10px] tracking-[0.32em] uppercase text-white/45">
            Menu
          </span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label={locale === "fr" ? "Fermer le menu" : "Close menu"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Liens */}
        <nav className="flex-1 overflow-y-auto px-7 py-8">
          <ul className="flex flex-col gap-7">
            {NAV_ITEMS.map((item, idx) => {
              const label = labels[item.key] ?? item.key;
              const active = isActiveItem(item.href);
              const reveal = {
                transitionDelay: mobileOpen ? `${120 + idx * 55}ms` : "0ms",
              };
              const revealCls = [
                "transition-all duration-500 ease-out",
                mobileOpen ? "translate-x-0 opacity-100" : "translate-x-6 opacity-0",
              ].join(" ");

              return (
                <li key={item.key} className={revealCls} style={reveal}>
                  {item.dropdown?.length ? (
                    <div>
                      <p className="text-[10px] tracking-[0.32em] uppercase text-white/40">
                        {label}
                      </p>
                      <ul className="mt-3 flex flex-col gap-3 border-l border-white/10 pl-4">
                        {item.dropdown.map((d) => (
                          <li key={d.href}>
                            <Link
                              href={`/${locale}${d.href}`}
                              className="font-heading text-2xl font-light text-white/85 transition hover:text-white"
                            >
                              {labels[d.key] ?? d.key}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <Link
                      href={`/${locale}${item.href}`}
                      className={[
                        "font-heading text-2xl font-light transition hover:text-white",
                        active ? "text-white italic" : "text-white/85",
                      ].join(" ")}
                    >
                      {label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Pied du panneau — CTA + langue */}
        <div
          className={[
            "border-t border-white/10 px-7 py-6 transition-all duration-500 ease-out",
            mobileOpen ? "translate-x-0 opacity-100" : "translate-x-6 opacity-0",
          ].join(" ")}
          style={{ transitionDelay: mobileOpen ? "520ms" : "0ms" }}
        >
          <Link
            href={`/${locale}/membership/join`}
            className="flex items-center justify-center rounded-full bg-white px-6 py-3 text-[11px] tracking-[0.2em] uppercase text-black transition hover:bg-white/90"
          >
            {labels.becomeMember}
          </Link>
          <div className="mt-5 flex items-center justify-center gap-3 text-[12px] font-light uppercase tracking-[0.08em] text-white/60">
            <Link
              href={linkForLocale("fr")}
              className={locale === "fr" ? "text-white" : "hover:text-white"}
            >
              FR
            </Link>
            <span className="text-white/30">/</span>
            <Link
              href={linkForLocale("en")}
              className={locale === "en" ? "text-white" : "hover:text-white"}
            >
              EN
            </Link>
          </div>
        </div>
      </aside>
    </header>
  );
}